import React, { useContext, useState, useEffect } from "react";
import { CardBody } from "reactstrap";
import echarts from "echarts/lib/echarts";
import ReactEchartsCore from "echarts-for-react/lib/core";
import { getGrays } from "../../../helpers/utils";
import AppContext from "../../../context/Context";
import emptyState from "../../../assets/img/illustrations/graphplaceholder.png";

import "echarts/lib/chart/bar";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/grid";

const truncateText = (text, maxLength = 150) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

const getOption = (
  categories,
  bgValues,
  values,
  colors,
  isDark,
  maxLength,
  valueLabel,
  barWidth,
) => {

  const grays = getGrays(isDark);
  const formatNumber = (num) => {
    if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, "") + "m";
    if (num >= 1e3) return (num / 1e3).toFixed(1).replace(/\.0$/, "") + "k";
    return num;
  };
  const truncatedCategories = categories.map(category => truncateText(category, maxLength));
  return {
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: {
        type: 'none'
      },
      formatter: function (params) {
        const dataIndex = params[0].dataIndex;
        return `${categories[dataIndex]}<br/>${valueLabel}: ${formatNumber(values[dataIndex])}`;
      }
    },
    grid: {
      left: 0,
      right: 0,
      top: 20,
      bottom: 20,
      containLabel: true
    },
    xAxis: {
      type: "value",
      show: false
    },
    yAxis: {
      type: "category",
      data: truncatedCategories,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false }
    },
    series: [
      {
        type: "bar",
        data: bgValues,
        barWidth,
        barGap: "-100%",
        barCategoryGap: "40%",
        itemStyle: {
          color: grays[200],
          barBorderRadius: [999, 999, 999, 999]
        },
        silent: true,
        z: 1
      },
      {
        type: "bar",
        data: values.map((value, index) => ({
          value,
          itemStyle: {
            color: colors[index] || "#1991ff",
            barBorderRadius: [999, 999, 999, 999]
          }
        })),
        barWidth,
        barGap: "-100%",
        barCategoryGap: "40%",
        z: 2
      },
      {
        type: "bar",
        data: bgValues,
        barWidth,
        barGap: "-100%",
        barCategoryGap: "40%",
        itemStyle: { color: "transparent" },
        label: {
          show: true,
          position: "insideeft",
          offset: [0, -20],
          formatter: params => `{cat|${truncatedCategories[params.dataIndex]}}`,
          rich: {
            cat: {
              color: "black",
              fontSize: 12,
              align: "left",
              fontFamily: "'DM Sans', Arial, sans-serif",
              width: maxLength * 5,
              overflow: 'truncate'
            }
          }
        },
        z: 3
      },
      {
        type: "bar",
        data: bgValues,
        barWidth,
        barGap: "-100%",
        barCategoryGap: "40%",
        itemStyle: { color: "transparent" },
        label: {
          show: true,
          position: "insideRight",
          offset: [0, -20],
          formatter: function (params) {
            const idx = params.dataIndex;
            return `{val|${formatNumber(values[idx])}}`;
          },
          rich: {
            val: {
              color: grays[700],
              fontSize: 12,
              align: "right"
            }
          }
        },
        z: 4
      }
    ]
  };
};

const HorizontalBarChart = ({
  categories = [],
  values = [],
  colors = [],
  title,
  valueLabel = "Value",
  showLoading = false,
  barWidth = 8,
}) => {
  const { isDark } = useContext(AppContext);
  const maxValue = Math.max(...values, 0);
  const adjustedMaxValue = maxValue <= 1 ? 5 : maxValue;
  const bgValues = categories.map(() => adjustedMaxValue * 1.25);
  const hasData = categories.length > 0 && values.length > 0;
  const chartHeight = categories.length === 1 ? 100 : categories.length * 70;

  const [containerWidth, setContainerWidth] = useState(0);
  const [chartContainer, setChartContainer] = useState(null);

  const getCharacterLimit = () => {
    if (containerWidth < 489) return 60;
    if (containerWidth < 768) return 90;
    if (containerWidth < 1200) return 120;
    return 150;
  };

  useEffect(() => {
    if (!chartContainer) return;
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    resizeObserver.observe(chartContainer);
    return () => {
      resizeObserver.disconnect();
    };
  }, [chartContainer]);

  return (
    <>
      {title && <h5>{title}</h5>}
      <div ref={el => setChartContainer(el)}>
        <CardBody className="p-0">
          {hasData ? (
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              <ReactEchartsCore
                showLoading={showLoading}
                echarts={echarts}
                option={getOption(
                  categories,
                  bgValues,
                  values,
                  colors,
                  isDark,
                  getCharacterLimit(),
                  valueLabel,
                  barWidth,
                )}
                style={{ height: `${chartHeight}px`, width: "100%" }}
              />
            </div>
          ) : (
            <div className="text-center p-5">
              <img src={emptyState} alt="No data" style={{ width: '150px', height: '150px' }} />
              <p>No data available</p>
            </div>
          )}
        </CardBody>
      </div>
    </>
  );
};

export default HorizontalBarChart;
